import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/components/Reveal";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Products", href: "#products" },
  { label: "Process", href: "#process" },
  { label: "Logistics", href: "#markets" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#2E7FE0]/15"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
        <button
          onClick={() => go("#top")}
          className="flex items-center group"
          data-testid="nav-logo-button"
        >
          <img src="/nexgen-logo-horizontal.png" alt="NexGen Sourcing Private Limited" className="h-9 sm:h-10 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-9" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#54678A] hover:text-[#0A1B33] transition-colors duration-300"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => go("#contact")}
            className="hidden lg:inline-flex items-center gap-2 bg-[#2E7FE0] hover:bg-[#4a93eb] text-white font-display font-medium text-sm px-6 py-2.5 rounded-full transition-colors duration-300"
            data-testid="nav-cta-button"
          >
            Start an Enquiry
            <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            className="lg:hidden text-[#0A1B33] p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-[#2E7FE0]/15"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-xl text-[#54678A] hover:text-[#0A1B33] transition-colors duration-300"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#2E7FE0] text-white font-display font-medium px-6 py-3 rounded-full"
                data-testid="nav-mobile-cta-button"
              >
                Start an Enquiry
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
